/**
 * MovementLane — Renders movement segments as coloured blocks
 *
 * Lane 1: MOVING (green), STOPPED (red), UNKNOWN (gray)
 */
import { useMemo } from 'react';
import type { MovementSegment, TimelineSelection, TimelineHover } from '../../../types/vehicleSceneTimeline';
import {
  timeWindowToRect,
  SEGMENT_COLORS,
  formatTime,
  formatDuration,
  toMs,
  type TimeScale,
} from '../../../utils/timelineUtils';

interface MovementLaneProps {
  segments: MovementSegment[];
  scale: TimeScale;
  height?: number;
  selection: TimelineSelection | null;
  hover: TimelineHover | null;
  onSelect: (segmentId: string) => void;
  onHover: (h: TimelineHover | null) => void;
}

const LANE_HEIGHT = 36;

export default function MovementLane({
  segments,
  scale,
  height = LANE_HEIGHT,
  selection,
  hover,
  onSelect,
  onHover,
}: MovementLaneProps) {
  const rects = useMemo(
    () =>
      segments.map(seg => {
        const rect = timeWindowToRect(seg.start_ts, seg.end_ts, scale);
        return { seg, ...rect };
      }),
    [segments, scale],
  );

  const isHighlighted = (segId: string): boolean => {
    if (!selection) return false;
    if (selection.type === 'segment' && selection.id === segId) return true;
    return selection.relatedSegmentIds?.includes(segId) ?? false;
  };

  const isHovered = (segId: string): boolean => {
    if (!hover) return false;
    return hover.relatedSegmentIds?.includes(segId) ?? false;
  };

  const isDimmed = (segId: string): boolean => {
    if (!selection) return false;
    return !isHighlighted(segId);
  };

  return (
    <div className="vst-lane" data-lane="movement">
      <div className="vst-lane-label">
        <span className="vst-lane-dot" style={{ background: '#22c55e' }} />
        Movement
      </div>
      <svg className="vst-lane-svg" width={scale.widthPx} height={height}>
        {rects.map(({ seg, x, width }) => (
          <g
            key={seg.segment_id}
            className="vst-lane-item"
            pointerEvents="auto"
            onClick={() => onSelect(seg.segment_id)}
            onMouseEnter={e =>
              onHover({
                type: 'segment',
                id: seg.segment_id,
                mouseX: e.clientX,
                mouseY: e.clientY,
              })
            }
            onMouseLeave={() => onHover(null)}
            style={{ cursor: 'pointer' }}
          >
            <rect
              x={x}
              y={4}
              width={width}
              height={height - 8}
              rx={3}
              fill={SEGMENT_COLORS[seg.type] || '#6b7280'}
              opacity={isDimmed(seg.segment_id) ? 0.25 : isHovered(seg.segment_id) ? 1 : 0.85}
              stroke={isHighlighted(seg.segment_id) ? '#fff' : 'none'}
              strokeWidth={isHighlighted(seg.segment_id) ? 2 : 0}
            />
            {/* Label if block is wide enough */}
            {width > 50 && (
              <text
                x={x + width / 2}
                y={height / 2 + 1}
                fill="#fff"
                fontSize={10}
                textAnchor="middle"
                dominantBaseline="middle"
                pointerEvents="none"
              >
                {seg.type}
              </text>
            )}
            <title>
              {`${seg.type} | ${formatTime(toMs(seg.start_ts))} – ${formatTime(toMs(seg.end_ts))} (${formatDuration(seg.start_ts, seg.end_ts)})\nSource: ${seg.source}\nConfidence: ${seg.confidence ? Math.round(seg.confidence * 100) + '%' : 'N/A'}${seg.notes ? '\n' + seg.notes : ''}`}
            </title>
          </g>
        ))}
      </svg>
    </div>
  );
}
